/**
 * MODULE: domain/sales-profile/frontend/api/client.ts
 * PURPOSE: API client for sales profile endpoints; includes Sales Hub getSalesHubContent (aggregates guides).
 * DEPENDENCIES: @shared/lib/frontend/api/config, @shared/lib/frontend/api/errors (getErrorMessage). See: /domain/sales-profile/README.md
 * AGENT SAFETY: SAFE
 * FILE SIZE: ~269 / 300 lines
 * Recent: 2026-02-24 - getErrorMessage for API error handling. 2026-02-05 - getSalesHubContent, SalesHubContentItem
 */

import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { getErrorMessage } from '@shared/lib/frontend/api/errors';
import type {
  CompanyProfile,
  CompanyProfileCreate,
  CompanyProfileUpdate,
  Product,
  ProductCreate,
  ProductUpdate,
  ProductListResponse,
  SalesGuide,
  SalesGuideCreate,
  SalesGuideUpdate,
  GuideListResponse,
  GuideType,
  SalesHubContentItem,
} from '../../contracts/sales-profile.types';

const API_BASE_URL = getApiBaseUrl();
const BASE_PATH = '/api/v1/sales-profile';

function getAuthHeaders(): HeadersInit {
  // Use same token keys as auth system: AuthProvider stores as 'access_token'
  const token =
    localStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(getErrorMessage(error, `HTTP ${response.status}`));
  }
  return response.json();
}

export const salesProfileApi = {
  // =====================================================
  // Company Profile
  // =====================================================

  async getCompanyProfile(): Promise<CompanyProfile> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/company`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<CompanyProfile>(response);
  },

  async createCompanyProfile(data: CompanyProfileCreate): Promise<CompanyProfile> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/company`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CompanyProfile>(response);
  },

  async updateCompanyProfile(data: CompanyProfileUpdate): Promise<CompanyProfile> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/company`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CompanyProfile>(response);
  },

  // =====================================================
  // Products
  // =====================================================

  async listProducts(includeInactive = false): Promise<ProductListResponse> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('include_inactive', 'true');
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/products?${params}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProductListResponse>(response);
  },

  async getProduct(productId: string): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/products/${productId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Product>(response);
  },

  async createProduct(data: ProductCreate): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Product>(response);
  },

  async updateProduct(productId: string, data: ProductUpdate): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/products/${productId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Product>(response);
  },

  async deleteProduct(productId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/products/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
      throw new Error(getErrorMessage(error, `HTTP ${response.status}`));
    }
  },

  // =====================================================
  // Sales Guides
  // =====================================================

  async listGuides(options?: {
    productId?: string;
    guideType?: GuideType;
    limit?: number;
    offset?: number;
  }): Promise<GuideListResponse> {
    const params = new URLSearchParams();
    if (options?.productId) params.set('product_id', options.productId);
    if (options?.guideType) params.set('guide_type', options.guideType);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/guides?${params}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<GuideListResponse>(response);
  },

  async getGuide(guideId: string): Promise<SalesGuide> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/guides/${guideId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<SalesGuide>(response);
  },

  async generateGuide(data: SalesGuideCreate): Promise<SalesGuide> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/guides/generate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<SalesGuide>(response);
  },

  async updateGuide(guideId: string, data: SalesGuideUpdate): Promise<SalesGuide> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/guides/${guideId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<SalesGuide>(response);
  },

  async deleteGuide(guideId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${BASE_PATH}/guides/${guideId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
      throw new Error(getErrorMessage(error, `HTTP ${response.status}`));
    }
  },

  // =====================================================
  // Sales Hub (aggregated content for hub grid)
  // =====================================================

  /**
   * Get Sales Hub content with filters. Aggregates guides (and optionally company/products) into a unified list.
   */
  async getSalesHubContent(params: {
    type?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: SalesHubContentItem[] }> {
    const limit = params.limit ?? 20;
    const search = (params.search ?? '').trim().toLowerCase();
    const typeFilter = params.type ?? 'all';

    const response = await this.listGuides({
      limit: 100,
      offset: 0,
    });

    const rawItems: SalesHubContentItem[] = (response.guides ?? []).map(
      (g: SalesGuide) => ({
        id: g.id,
        title: g.title ?? `${g.guide_type ?? 'Guide'}`,
        description:
          g.summary ??
          ((Array.isArray(g.sections) && g.sections[0]
            ? String((g.sections[0] as { content?: string }).content ?? '').slice(0, 200)
            : '') || 'No description'),
        type: 'sales-guides',
        tags: Array.isArray(g.tags) ? g.tags : [],
        lastUpdated: g.updated_at ?? g.created_at ?? '',
        author: g.created_by ?? 'Unknown',
      })
    );

    let filtered = rawItems;

    if (typeFilter !== 'all') {
      if (typeFilter === 'sales-guides') {
        filtered = rawItems;
      } else {
        // Other types (company-info, products, etc.) could be added when backend supports them
        filtered = typeFilter === 'sales-guides' ? rawItems : [];
      }
    }

    if (search) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(search) ||
          item.description.toLowerCase().includes(search) ||
          item.tags.some((t) => t.toLowerCase().includes(search))
      );
    }

    const start = ((params.page ?? 1) - 1) * limit;
    const data = filtered.slice(start, start + limit);

    return { data };
  },

  // SSE streaming for guide generation
  createGuideStream(productId: string, guideType: GuideType): EventSource {
    const token =
      localStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token') ||
      '';
    const params = new URLSearchParams({
      product_id: productId,
      guide_type: guideType,
      token,
    });
    return new EventSource(`${API_BASE_URL}${BASE_PATH}/guides/stream?${params}`);
  },
};

export default salesProfileApi;
